import { useQuery } from "@tanstack/react-query";
import { Loader2, User } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const cr = (v) => (Number(v || 0) / 1e7).toFixed(2);
const lac = (v) => (Number(v || 0) / 1e7).toFixed(2);
const inr = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const smart = (v) => cr(v);
const CLOSED = ["close", "closed", "paid"];
const today = new Date().toISOString().slice(0, 10);

function Tile({ label, value, cls, testId }) {
  return (
    <div className={`rounded-lg px-3 py-2 ${cls}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</p>
      <p className="font-mono font-bold text-sm">{value}</p>
    </div>
  );
}

export function CustomerDrillDown({ name, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["billing-customer", name],
    queryFn: async () => (await api.get("/billing/customer", { params: { name } })).data,
    enabled: !!name,
  });
  const s = data?.summary || {};
  const months = (data?.by_month || []).map((m) => ({
    m: new Date(+m.month.slice(0, 4), +m.month.slice(5, 7) - 1, 1).toLocaleString("en", { month: "short" }),
    v: +(m.taxable / 1e7).toFixed(2),
  }));
  return (
    <Dialog open={!!name} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="customer-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><User className="h-4 w-4 text-brand" /> {name}</DialogTitle>
          <DialogDescription className="text-xs">
            {data ? `${s.invoices} invoices · billed ${smart(s.invoiced)} (incl. tax)` : "Loading payment history…"}
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Tile label="Invoiced" value={smart(s.invoiced)} cls="bg-slate-100 text-slate-700" testId="cust-invoiced" />
              <Tile label="Received" value={smart(s.received)} cls="bg-emerald-50 text-emerald-700" testId="cust-received" />
              <Tile label="Outstanding" value={smart(s.outstanding)} cls="bg-amber-50 text-amber-700" testId="cust-outstanding" />
              <Tile label={`Overdue (${s.overdue_invoices})`} value={smart(s.overdue)} cls="bg-red-50 text-red-700" testId="cust-overdue" />
            </div>
            {months.length > 1 && (
              <div style={{ height: 120 }} className="mt-1">
                <ResponsiveContainer>
                  <BarChart data={months} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="m" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 9 }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [v, "Taxable (INR Cr)"]} />
                    <Bar dataKey="v" fill="#3b9fe0" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  {["Invoice", "Date", "Project", "Value", "Received", "Outstanding", "Due", "Status"].map((h) => (
                    <TableHead key={h} className="text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                  ))}
                </TableRow></TableHeader>
                <TableBody>
                  {data.invoices.map((i, idx) => {
                    const closed = CLOSED.includes((i.status || "").toLowerCase());
                    const overdue = !closed && i.outstanding > 0 && i.due_date && i.due_date < today;
                    const days = overdue ? Math.floor((Date.now() - new Date(i.due_date).getTime()) / 86400000) : 0;
                    return (
                      <TableRow key={i.invoice_no} data-testid={`cust-invoice-${idx}`}>
                        <TableCell className="font-mono text-xs">{i.invoice_no}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{i.date}</TableCell>
                        <TableCell className="text-xs max-w-[150px] truncate text-slate-500">{i.project}</TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold">{inr(i.value)}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-emerald-700">{inr(i.received)}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-amber-700">{inr(i.outstanding)}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{i.due_date || "—"}</TableCell>
                        <TableCell>
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${
                            overdue ? "bg-red-100 text-red-700" : closed ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-700"}`}>
                            {overdue ? `Overdue ${days}d` : i.status || "—"}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
